import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { CORE_SERVICE_URLS } from '~/Constants/Urls.constants';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { routeReplacer, createQueriedURL } from '~/Helpers/String.helper';
import { useProvider, useDateFns } from '~/Hooks';
import { IAttendance } from '~/Models/DTO/Attendance.model';
import { IErrorResponse } from '~/Models/DTO/Error.models';

export interface IGetAttendanceListProps {
  page?: number;
  pageSize?: number;
  activateLast?: boolean;
  locationIds?: string[];
  eventIds?: string[];
  searchText?: string;
  date?: Date[];
  status?: string;
}

interface IAttendanceFilter {
  providerId: string | undefined;
  page?: number;
  pageSize?: number;
  locationIds?: string[];
  eventGroupIds?: string[];
  bookingStates?: string;
  customerName?: string;
  from?: string;
  to?: string;
}

const retriveCheckins = ({ providerId, retrieveResponseHeader, ...params }: any) =>
  requestReactQuery<IAttendance[]>({
    url: createQueriedURL(
      routeReplacer(CORE_SERVICE_URLS.GET_ATTENDANCE_LIST, { providerId }),
      params,
    ),
    retrieveResponseHeader,
    config: { method: 'GET' },
  });

const sortListByDate = (list: IAttendance[] | undefined) =>
  list
    ? [...list].sort((prev, current) => Number(current.checkInTime) - Number(prev.checkInTime))
    : undefined;

export const useRequestAttendanceList = ({
  activateLast,
  locationIds,
  eventIds,
  status,
  searchText,
  page,
  pageSize,
  date,
}: IGetAttendanceListProps) => {
  const { currentProvider } = useProvider();
  const { formatDate } = useDateFns();
  const [erroredBookings, setErroredBookings] = useState<IAttendance[]>();
  const [responseHeaders, setHeaders] = useState();
  const [isLoading, setLoading] = useState(true);
  const retrieveResponseHeader = useCallback(setHeaders, []);
  const filterObject: IAttendanceFilter = {
    providerId: currentProvider?.uuid,
    page,
    pageSize,
  };

  if (eventIds?.length) {
    filterObject.eventGroupIds = eventIds;
  }
  if (locationIds?.length) {
    filterObject.locationIds = locationIds;
  }
  if (status?.length) {
    filterObject.bookingStates = status;
  }
  if (searchText?.length) {
    filterObject.customerName = searchText;
  }
  if (date?.[0] && date?.[1]) {
    filterObject.from = `${formatDate(date[0].toString(), 'yyyy-MM-dd')}T00:00:00.000Z`;
    filterObject.to = `${formatDate(date[1].toString(), 'yyyy-MM-dd')}T22:59:59.000Z`;
  }

  const bookings = useQuery<IAttendance[], IErrorResponse>(
    [
      REQUEST_ACTIONS.ATTENDANCE_LIST,
      [searchText, locationIds?.toString(), eventIds?.toString(), date?.toString()],
    ],
    () => retriveCheckins({ ...filterObject, retrieveResponseHeader }),
    {
      refetchOnWindowFocus: false,
      refetchInterval: 5000,
      keepPreviousData: true,

      initialData: [],
    },
  );

  useEffect(() => {
    setLoading(true);
  }, [JSON.stringify(filterObject)]);

  useEffect(() => {
    if (!bookings.isFetching && !bookings.isLoading) {
      setLoading(false);
      setErroredBookings(
        bookings.data?.filter((item: any) => item.checkInStatus === 'Errored') || [],
      );
    }
  }, [bookings.isFetching, bookings.isLoading, bookings.data]);

  bookings.data = sortListByDate(bookings?.data);
  bookings.data = bookings.data?.map((item: any, index) => ({
    ...item,
    active: activateLast && !index,
  }));

  const newErroredBookings =
    !!erroredBookings &&
    bookings?.data?.filter(
      (item: any) =>
        item.checkInStatus === 'Errored' &&
        !erroredBookings?.find(
          (erroredItem: any) => erroredItem.pendingBookingId === item.pendingBookingId,
        ),
    );

  return {
    ...bookings,
    isLoading,
    data: bookings?.data?.filter((item: any) => item.checkInStatus !== 'Errored'),
    newErroredBookings,
    setErroredBookings,
    totalCount: responseHeaders?.['x-total-count'],
  } as any;
};
